/* eslint-disable @typescript-eslint/ban-ts-comment */
import { CaretDoubleLeft, CaretDown } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'motion/react';
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import type { ThemeId } from '../../constants';
import { useMedia } from '../../hooks/useMedia';
import { useTopPositions } from '../../hooks/useTopPositions';
import { colors } from '../../styles/variables';
import { PageRoute } from '../../types';
import { Footer } from '../Footer';
import { Paragraph } from '../Typography';

const ICON_SIZE = 24;
const ENTITLEMENTS = 'entitlements';

// max-width/min-width must stay the animated properties: portal's Navigation
// measures the rail via useComponentWidth's transitionend listener on them.
const ROOT =
  'flex flex-col box-border h-full w-full text-(length:--size-4) ' +
  'transition-[max-width,min-width] duration-200 ease-[ease]';
const ROOT_EXPANDED = 'max-w-[245px] min-w-[185px]';
const ROOT_MINIMIZED =
  'max-w-[85px] min-w-[100px] [@media(max-width:768px)]:min-w-[60px]';

const LOGO_CARET =
  'flex items-center justify-center px-6 [@media(max-width:768px)]:hidden';
const LOGO_CARET_MINIMIZED = 'flex-col pt-[14px]';

// Per-side paddings only — no shorthands — so the media variants layer cleanly.
// `!` on the max-height:620px pair reproduces the stylesheet's source order,
// where the short-viewport block won over the narrow-viewport block.
const SCROLL =
  'flex h-full flex-col gap-3 [@media(max-height:750px)]:overflow-y-auto';
// No bottom padding here — the footer's own wrapper owns that offset.
const SCROLL_EXPANDED = 'px-6 pt-8';
const SCROLL_MINIMIZED =
  'px-8 pt-8 ' +
  '[@media(max-width:768px)]:pt-0 [@media(max-width:768px)]:pr-2.5 ' +
  '[@media(max-width:768px)]:pl-[15px] ' +
  '[@media(max-height:620px)]:px-6! [@media(max-height:620px)]:pt-6!';

const COLOR_DEFAULT = 'text-(--color-font-primary)';
const COLOR_BRAND = 'text-(--color-brand-primary)';
const COLOR_DISABLED = 'text-[#5e656c]';
const ITEM_ACTIVE = 'bg-(--color-side-menu-active-bg-item) font-bold';
// Hover previews the active row's background — same token — instead of
// bolding the label: with static 500/700 font instances a weight change can
// only snap (see the Font Weight Transitions brain note), and it also
// shifted the label width on hover.
const ITEM_HOVER = 'hover:bg-(--color-side-menu-active-bg-item)';

const MENU_ITEM =
  'flex items-center gap-2 h-8 px-1.5 rounded-lg leading-6 ' +
  'font-(family-name:--font-light) focus-visible:focus-ring';
const MENU_ITEM_FONT_SIZE = 'text-(length:--size-4)';
const MENU_ITEM_SUB = 'relative text-sm ml-6 mt-3';

const OPENER =
  'h-14 m-auto ml-2.5 bg-transparent border-none ' +
  '[&>svg]:transition-all [&>svg]:duration-200 [&>svg]:ease-[ease-in-out] ' +
  '[&_polyline]:[stroke-width:20]';
const OPENER_MINIMIZED =
  '[&>svg]:rotate-180 [@media(max-height:620px)]:ml-[5px]';

const MENU_ITEM_ICON =
  'mr-[3px] shrink-0 rounded-[100%] transition-all duration-200 ' +
  'ease-[ease-in-out] ' +
  'focus-visible:focus-ring';

const MENU_ITEM_LABEL =
  'flex items-center justify-center overflow-hidden text-ellipsis ' +
  'whitespace-nowrap select-none';

const MENU_ITEM_CARET =
  'flex items-center justify-center ml-6 [@media(max-height:750px)]:ml-[14px]';

const BRAND_TEXT =
  'ml-2.5 text-base not-italic font-bold leading-6 tracking-[0.32px] ' +
  COLOR_BRAND;

// CSS triangle pointing at the active sub-menu row.
const SUBMENU_CARET =
  'block absolute top-1.5 h-0 w-0 rotate-[150deg] border-solid ' +
  '[border-width:0_12px_20.8px_12px] ' +
  '[border-color:transparent_transparent_var(--ui-bg-dark)_transparent]';

const cx = (...classes: (string | false | undefined)[]) => {
  return classes.filter(Boolean).join(' ');
};

interface Props {
  routes: [string, PageRoute][];
  logoURL: string;
  isMinimized: boolean;
  handleMinimized: (value: boolean) => void;
  height?: string | undefined;
  padding?: string | undefined;
  roleValidations?: ((...args: unknown[]) => unknown)[];
  logo: string;
  logoMinimized: string;
  context?: ThemeId;
  poweredBy?: string;
  showCaretForRoutes?: string[];
}

const tooltipInitialState = {
  visible: false,
  label: '',
  position: { x: 0, y: 0 },
};

export const SideMenu = ({
  routes,
  logoURL,
  isMinimized = false,
  handleMinimized,
  height = undefined,
  padding = undefined,
  roleValidations,
  logo,
  logoMinimized,
  context,
  poweredBy,
  showCaretForRoutes,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [tooltipConfig, setTooltipConfig] = useState(tooltipInitialState);

  const isWide = useMedia('(min-width: 1281px)');

  const { t } = useTranslation();

  const entitlementsSubMenuCaret = useRef(null);
  const classPositions = routes
    .map(([, { useTopPositionClassName }]) => useTopPositionClassName)
    .filter((className): className is string => {
      return Boolean(className);
    });

  // One stable ref object per positioned route, which is the array shape
  // useTopPositions documents. Held in a memo rather than inside a single
  // useRef array so nothing reads `.current` during render.
  const positionsKey = classPositions.join('|');
  const positionRefs = useMemo(() => {
    return classPositions.map(() => {
      return { current: null as HTMLElement | null };
    });
    // keyed by the joined names: classPositions is rebuilt on every render, so
    // depending on it directly would hand out new ref objects each time
  }, [positionsKey]);

  useTopPositions(positionRefs, classPositions);

  const [showSubMenu, setShowSubMenu] = useState(true);

  const activePath = location.pathname;

  const isActiveRoute = (url: string) => activePath.includes(url);
  const isActiveRouteExact = (url: string) => activePath === url;

  const isEntitlementsPage = activePath.includes(ENTITLEMENTS);

  const style = { height, padding };

  // Both of the following adjust state when an input changes, using React's
  // documented render-time pattern instead of an effect. On mount each branch
  // would be a no-op (the state already holds the value it would set), so the
  // previous effect-based versions behaved identically.
  const [prevPath, setPrevPath] = useState(activePath);

  if (prevPath !== activePath) {
    setPrevPath(activePath);

    if (isEntitlementsPage) {
      setShowSubMenu(true);
    }
  }

  // Reset tooltip config when resizing from wide to narrow to prevent tooltip from showing in wrong position
  const [prevMinimized, setPrevMinimized] = useState(isMinimized);

  if (prevMinimized !== isMinimized) {
    setPrevMinimized(isMinimized);

    if (isMinimized) {
      setTooltipConfig(tooltipInitialState);
    }
  }

  useEffect(() => {
    if (isWide) {
      handleMinimized(false);
    } else {
      handleMinimized(true);
    }
  }, [isWide]);

  // Single source for the colour of a row — never two colour utilities at once.
  const getItemColorClass = (isDisabled: boolean, isBrandColored = false) => {
    if (isDisabled) {
      return COLOR_DISABLED;
    }

    if (isBrandColored) {
      return COLOR_BRAND;
    }

    return COLOR_DEFAULT;
  };

  const goToRoute = (url: string) => {
    if (isActiveRoute(url)) {
      return;
    }

    navigate(url);
  };

  const goToExactRoute = (url: string) => {
    if (isActiveRouteExact(url)) {
      return;
    }

    navigate(url);
  };

  const showCaretOnActiveRoute = (route: string) => {
    return showCaretForRoutes?.includes(route);
  };

  const handleToggleSubMenuOnKeyboard = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();

      if (entitlementsSubMenuCaret) {
        setShowSubMenu(!showSubMenu);
      }
    }
  };

  const onPressMenuKeyboard = (
    e: KeyboardEvent<HTMLDivElement>,
    url: string,
  ) => {
    if (e.key === ' ' || e.key === 'Enter') {
      goToRoute(url);
    }
  };

  const renderMenuItems = () => {
    return (
      routes
        .filter(([, { showOnlyOnDevMode }]) => {
          if (showOnlyOnDevMode) {
            return process.env.NODE_ENV === 'development';
          }
          return true;
        })
        .filter(([, { showInMenu }]) => showInMenu)
        // Number() keeps the original comparison semantics — see renderSubMenuItems
        .sort(([, { order: a }], [, { order: b }]) =>
          Number(a) > Number(b) ? 1 : -1,
        )
        .map(
          ([
            key,
            {
              url,
              icon,
              displayName = '',
              permission,
              useTopPositionClassName,
              subMenu,
            },
          ]) => {
            const Icon = icon;

            const handleOnClick = () => {
              if (isMinimized && key === ENTITLEMENTS) {
                handleMinimized(false);
              }
              goToRoute(url);
            };

            const can = roleValidations ? roleValidations[0] : null;

            const isDisabled = Boolean(
              roleValidations && permission && can && !can(permission),
            );

            const isActive = isActiveRoute(url);

            const activeClasses = cx(
              MENU_ITEM,
              MENU_ITEM_FONT_SIZE,
              getItemColorClass(isDisabled),
              isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
              isActive && ITEM_ACTIVE,
              !isDisabled && ITEM_HOVER,
              // sat above the sibling rows so the legacy hover tooltip escaped them
              isMinimized && 'z-[1000]',
            );

            const color = isDisabled
              ? colors.secondaryFontColor
              : colors.grey900;

            // `icon` is optional on PageRoute; rendering an undefined component
            // would throw React's "Element type is invalid"
            const iconElement = Icon ? (
              <Icon
                className={MENU_ITEM_ICON}
                size={ICON_SIZE}
                color={color}
                aria-label={`icon for ${t(displayName)}`}
              />
            ) : null;

            return (
              <div
                role="listitem"
                key={`menuItems_${key}`}
                ref={
                  useTopPositionClassName
                    ? el => {
                        const index = classPositions.indexOf(
                          useTopPositionClassName,
                        );

                        if (index !== -1) {
                          positionRefs[index].current = el;
                        }
                      }
                    : undefined
                }
              >
                {key === 'support' && (
                  <div className="border-t border-gray-200 my-6" />
                )}

                {key === 'codex' && (
                  <div className="border-t border-gray-200 my-6" />
                )}

                <div
                  role="button"
                  className={activeClasses}
                  aria-label={t(displayName)}
                  onClick={isDisabled ? undefined : handleOnClick}
                  onKeyDown={e => onPressMenuKeyboard(e, url)}
                  tabIndex={0}
                  data-testid="side-menu-item"
                  aria-disabled={isDisabled}
                >
                  {isMinimized ? (
                    <div
                      className="relative"
                      onMouseEnter={event => {
                        // Get element position on the screen
                        const target = event.currentTarget;
                        const rect = target.getBoundingClientRect();

                        setTooltipConfig({
                          visible: true,
                          label: displayName,
                          position: {
                            x: rect.right + 10,
                            y: rect.top + rect.height / 2 - 22.5,
                          },
                        });
                      }}
                      onMouseLeave={() => {
                        setTooltipConfig(tooltipInitialState);
                      }}
                    >
                      {iconElement}
                      {subMenu && showCaretOnActiveRoute(activePath) && (
                        <div className={cx(SUBMENU_CARET, '-right-[43px]')} />
                      )}
                    </div>
                  ) : (
                    <>
                      {iconElement}
                      <div
                        className={MENU_ITEM_LABEL}
                        role="button"
                        onClick={
                          key === ENTITLEMENTS
                            ? () => {
                                if (isDisabled) {
                                  return;
                                }
                                setShowSubMenu(!showSubMenu);
                              }
                            : undefined
                        }
                        onKeyDown={
                          key === ENTITLEMENTS
                            ? e => {
                                if (isDisabled) {
                                  return;
                                }
                                handleToggleSubMenuOnKeyboard(e);
                              }
                            : undefined
                        }
                      >
                        {key === ENTITLEMENTS ? (
                          <>
                            {t(displayName)}
                            <span className={MENU_ITEM_CARET}>
                              <CaretDown
                                className={cx(
                                  MENU_ITEM_ICON,
                                  showSubMenu && 'rotate-180',
                                )}
                                size={10}
                                color={color}
                                aria-label="close submenu icon"
                                tabIndex={0}
                                ref={entitlementsSubMenuCaret}
                                aria-disabled={isDisabled}
                              />
                            </span>
                          </>
                        ) : (
                          <>{t(displayName)}</>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {key === ENTITLEMENTS &&
                  showSubMenu &&
                  !isMinimized &&
                  renderSubMenuItems()}
              </div>
            );
          },
        )
    );
  };

  const renderSubMenuItems = () => {
    return (
      routes
        .filter(([, { subMenu }]) => subMenu)
        // Number() rather than `?? 0`: subMenuOrder is optional, and NaN
        // comparisons are false exactly like undefined ones, so ordering is
        // unchanged even for negative order values.
        .sort(([, { subMenuOrder: a }], [, { subMenuOrder: b }]) =>
          Number(a) > Number(b) ? 1 : -1,
        )
        .map(
          ([
            key,
            {
              url,
              displayName = '',
              subMenuOrderDisplayName,
              permission,
              useTopPositionClassName,
            },
          ]) => {
            const handleOnClick = () => {
              goToExactRoute(url);
            };

            const can = roleValidations ? roleValidations[0] : null;

            const isDisabled = Boolean(
              roleValidations && permission && can && !can(permission),
            );

            const activeClasses = cx(
              MENU_ITEM,
              MENU_ITEM_SUB,
              getItemColorClass(isDisabled, isActiveRouteExact(url)),
              isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
              isMinimized && 'hidden',
            );

            return (
              <div
                role="button"
                key={`menuItems_${key}`}
                className={activeClasses}
                ref={
                  useTopPositionClassName
                    ? el => {
                        const index = classPositions.indexOf(
                          useTopPositionClassName,
                        );

                        if (index !== -1) {
                          positionRefs[index].current = el;
                        }
                      }
                    : undefined
                }
                onClick={() => {
                  if (isDisabled) {
                    return null;
                  }

                  handleOnClick();
                }}
                // @ts-ignore
                alt={t(displayName)}
                tabIndex={0}
                onKeyDown={e => {
                  if (isDisabled) {
                    return;
                  }

                  onPressMenuKeyboard(e, url);
                }}
                color={isDisabled ? colors.secondaryFontColor : colors.grey900}
                aria-disabled={isDisabled}
              >
                <div className={MENU_ITEM_LABEL}>
                  {subMenuOrderDisplayName
                    ? t(subMenuOrderDisplayName)
                    : t(displayName)}
                </div>
                {/* Show/Hide Caret on active routes */}
                {showCaretOnActiveRoute(activePath) &&
                  isActiveRouteExact(url) && (
                    <div className={cx(SUBMENU_CARET, '-right-[34px]')} />
                  )}
              </div>
            );
          },
        )
    );
  };

  const renderLogo = () => {
    return (
      <Link tabIndex={0} to={logoURL} className="focus-visible:focus-ring">
        <figure className="flex m-0" style={style}>
          <img
            alt="logo"
            className="h-full w-40 object-contain data-[hidden=true]:hidden"
            src={logo}
            data-hidden={isMinimized}
          />
          <img
            alt="logo"
            className="h-full w-40 object-contain data-[hidden=true]:hidden"
            src={logoMinimized}
            data-hidden={!isMinimized}
            aria-hidden="true"
          />
        </figure>
      </Link>
    );
  };

  const toggleMinimized = () => {
    handleMinimized?.(!isMinimized);
  };

  const renderOpenCloseIcon = () => {
    return (
      <button
        type="button"
        className={cx(
          MENU_ITEM,
          MENU_ITEM_FONT_SIZE,
          getItemColorClass(false),
          'cursor-pointer',
          OPENER,
          isMinimized && OPENER_MINIMIZED,
        )}
        onClick={toggleMinimized}
        data-testid="side-menu-toggle"
        tabIndex={1}
        aria-expanded={isMinimized ? 'false' : 'true'}
      >
        <CaretDoubleLeft
          size={20}
          color={colors.grey900}
          weight="thin"
          aria-label={isMinimized ? 'open menu icon' : 'close menu icon'}
        />
      </button>
    );
  };

  return (
    <nav className={cx(ROOT, isMinimized ? ROOT_MINIMIZED : ROOT_EXPANDED)}>
      <div
        className={cx(LOGO_CARET, isMinimized && LOGO_CARET_MINIMIZED)}
        data-testid="side-menu-logo-caret"
      >
        {renderLogo()}
        {renderOpenCloseIcon()}
      </div>
      <div className="flex-1 min-h-0">
        {/*
         * Custom tooltip implementation to avoid overflow issues with SideMenu and Tooltip components
         */}
        <AnimatePresence>
          {tooltipConfig.visible && isMinimized && (
            <motion.div
              role="tooltip"
              className="fixed z-10 rounded-sm px-2.5 py-2 bg-(--color-font-primary) w-max"
              style={{
                top: tooltipConfig.position.y,
                left: tooltipConfig.position.x,
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              aria-live="polite"
            >
              <Paragraph size="sm" tag="span" className="text-white!">
                {t(tooltipConfig.label)}
              </Paragraph>
            </motion.div>
          )}
        </AnimatePresence>
        <div
          role="list"
          className={cx(
            SCROLL,
            isMinimized ? SCROLL_MINIMIZED : SCROLL_EXPANDED,
          )}
          tabIndex={-1}
        >
          {context === 'gd' && !isMinimized && (
            <p className={BRAND_TEXT}>AirOn360® ES</p>
          )}
          {renderMenuItems()}
          <div className="h-10" />
          {poweredBy && !isMinimized && (
            // mt-auto pins it to the bottom of the rail, pb-4 keeps it off the edge
            <div className="mt-auto pb-4">
              <Footer>{poweredBy}</Footer>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};
